import { cn } from '@commenda/utils';
import React from 'react';

import { Button } from '../../Button';
import { Dropdown, type DropdownMenuProps } from '../../Dropdown';
import { IconButton } from '../../IconButton';
import { Icons } from '../../Icons';
import { Switch } from '../../Switch';
import { DataGridStateAction } from '../types';

export interface DataGridCellActionsIcon {
  key: string;
  icon: React.ReactNode;
  ariaLabel: string;
  onClick: () => void;
}

export interface DataGridCellActionsMenuProps {
  kind: 'menu';
  primary?: DataGridStateAction;
  menu?: DropdownMenuProps;
  menuAriaLabel: string;
  className?: string;
}

export interface DataGridCellActionsIconsProps {
  kind: 'icons';
  actions: DataGridCellActionsIcon[];
  className?: string;
}

export interface DataGridCellActionsToggleProps {
  kind: 'toggle';
  isOn: boolean;
  onToggle: (next: boolean) => void;
  ariaLabel: string;
  className?: string;
}

export type DataGridCellActionsProps =
  | DataGridCellActionsMenuProps
  | DataGridCellActionsIconsProps
  | DataGridCellActionsToggleProps;

const DATA_GRID_CELL_ACTIONS_CLASS_NAMES =
  'flex w-full items-center justify-end';

const DataGridCellActions = React.forwardRef<
  HTMLDivElement,
  DataGridCellActionsProps
>((props, ref) => {
  if (props.kind === 'toggle') {
    return (
      <div
        ref={ref}
        className={cn(
          DATA_GRID_CELL_ACTIONS_CLASS_NAMES,
          'gap-lg',
          props.className
        )}
      >
        <Switch
          checked={props.isOn}
          onCheckedChange={props.onToggle}
          aria-label={props.ariaLabel}
        />
      </div>
    );
  }

  if (props.kind === 'icons') {
    return (
      <div
        ref={ref}
        className={cn(
          DATA_GRID_CELL_ACTIONS_CLASS_NAMES,
          'gap-xs',
          props.className
        )}
      >
        {props.actions.map((action) => (
          <IconButton
            key={action.key}
            size="sm"
            variant="default"
            onClick={action.onClick}
            aria-label={action.ariaLabel}
            icon={action.icon}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        DATA_GRID_CELL_ACTIONS_CLASS_NAMES,
        'gap-xs',
        props.className
      )}
    >
      {props.primary ? (
        <Button variant="secondary" size="sm" onClick={props.primary.onClick}>
          {props.primary.text}
        </Button>
      ) : null}
      {props.menu ? (
        <Dropdown menu={props.menu} align="end">
          <IconButton
            size="lg"
            variant="default"
            aria-label={props.menuAriaLabel}
            icon={<Icons.DotsVertical className="size-5" />}
          />
        </Dropdown>
      ) : null}
    </div>
  );
});

DataGridCellActions.displayName = 'DataGridCellActions';

export { DataGridCellActions };
